//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { AccessGuids, ConfigSystemParams, eSimApiPaths, StorageValues } from 'src/app/core/consts';
import { AuthenticationService } from '../../../auth.service';
import { ConfigSystemService } from '../config-system/config-system-service';
import { EncryptAesClientSide } from 'src/app/core/encryption';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from '../../general/exceptions-service';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { GlobalEventService } from '../../general/global-event-service';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

@Injectable({ providedIn: 'root' })

export class ComponentAccessUserService {
  
  private curUser!: SystemUserDTO;

  public userAccessGuids: string[] | undefined;

  constructor(
    private authenticationService: AuthenticationService,
    private configSystemService  : ConfigSystemService,
    private encryptionService    : EncryptionService,
    private exceptionsService    : ExceptionsService,
    private globalEventService   : GlobalEventService,
    private httpClientService    : HttpClientService
  ) {
    //CRN_20250221 - Once this retrieves a value it holds it regardless of logging off and on
    //             - This only fires once, as it is a 'root' service
    //this.curUser=this.authenticationService.currentUserObject();
  }

  public applyLocalAccessGuidChange(guid: string, active: boolean): void {
    try {
      if (!guid)
        return;

      if (this.userAccessGuids==undefined)
        this.userAccessGuids=[];

      const guidUpper    =guid.toUpperCase();
      const existingIndex=this.userAccessGuids.findIndex(x => x.toUpperCase()==guidUpper);
      const alreadyHasIt =existingIndex>=0;
      let   mutated      =false;

      if (active && !alreadyHasIt) {
        this.userAccessGuids.push(guid);
        mutated=true;
      }
      else if (!active && alreadyHasIt) {
        this.userAccessGuids.splice(existingIndex, 1);
        mutated=true;
      }

      if (mutated) {
        if (guidUpper==AccessGuids.LoginCookieWarningSuppress.toUpperCase()) {
          if (active)
            localStorage.setItem(StorageValues.encLoginCookieWarningSuppress, EncryptAesClientSide("true"));
          else
            localStorage.removeItem(StorageValues.encLoginCookieWarningSuppress);
        }

        this.globalEventService.broadcastAccessGuidsChanged();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public getAccessGuids(companyID: number, userID: number): Observable<string[]> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyID, true),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userID)
             })
             .pipe(
               switchMap(({ companyIdEncResult, userIdEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;
                 const userIdEnc     =userIdEncResult.result;

                 return this.httpClientService.get<string[]>(environment.eSimApi +
                                                             eSimApiPaths.ComponentAccess_User + "/" +
                                                             eSimApiPaths.ComponentAccess_UserAndCompanyDefaults +
                                                             "?companyIdEncSS=" + companyIdEncSS +
                                                             "&userIdEnc="      + userIdEnc
                                                            );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getCompanyHomeGuardState(companyId: number): Observable<{ result: boolean }> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ companyIdEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;

                 return this.httpClientService.get<{ result: boolean }>(environment.eSimApi +
                                                                        eSimApiPaths.ComponentAccess_Company_Actual + "/" +
                                                                        eSimApiPaths.ComponentAccess_Company_Actual_HomeGuardState +
                                                                        "?companyIdEncSS=" + companyIdEncSS
                                                                       );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of({ result: false });
    }
  }

  public getUserHomeGuardState(userId: number): Observable<{ result: boolean }> {
    try {
      return forkJoin({
               userIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             })
             .pipe(
               switchMap(({ userIdEncResult }) => {
                 const userIdEncSS=userIdEncResult.result;

                 return this.httpClientService.get<{ result: boolean }>(environment.eSimApi +
                                                                        eSimApiPaths.ComponentAccess_User_Actual + "/" +
                                                                        eSimApiPaths.ComponentAccess_User_Actual_HomeGuardState +
                                                                        "?userIdEncSS=" + userIdEncSS
                                                                       );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of({ result: false });
    }
  }

  public setCompanyHomeGuard(companyId: number, enabled: boolean): Observable<Boolean> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ companyIdEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;

                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.ComponentAccess_Company_Actual + "/" +
                                                    eSimApiPaths.ComponentAccess_Company_Actual_SetHomeGuard +
                                                    "?companyIdEncSS=" + companyIdEncSS +
                                                    "&enabled="        + enabled,
                                                    ""
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public setUserHomeGuard(userId: number, enabled: boolean): Observable<Boolean> {
    try {
      return forkJoin({
               userIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             })
             .pipe(
               switchMap(({ userIdEncResult }) => {
                 const userIdEncSS=userIdEncResult.result;

                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.ComponentAccess_User_Actual + "/" +
                                                    eSimApiPaths.ComponentAccess_User_Actual_SetHomeGuard +
                                                    "?userIdEncSS=" + userIdEncSS +
                                                    "&enabled="     + enabled,
                                                    ""
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public clearUserHomeGuard(userId: number): Observable<Boolean> {
    try {
      return forkJoin({
               userIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             })
             .pipe(
               switchMap(({ userIdEncResult }) => {
                 const userIdEncSS=userIdEncResult.result;

                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.ComponentAccess_User_Actual + "/" +
                                                    eSimApiPaths.ComponentAccess_User_Actual_ClearHomeGuard +
                                                    "?userIdEncSS=" + userIdEncSS,
                                                    ""
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public showUsageDashboard(): boolean {
    return this.configSystemService.getValueAsBoolean(ConfigSystemParams.MenuShowNewDashboard, false)
           || this.hasAccessGuid(AccessGuids.DashboardShowUsageDashboard, true);
  }

  public get dashboardUrl(): string {
    return this.showUsageDashboard() ? '/dashboard-usage' : '/dashboard-main';
  }

  public hasAccessGuid(guid: string, doTrueCheck: boolean=false): boolean {
    try {
      const guidUpper                =guid?.toUpperCase();
      const _guid: string | undefined=this.userAccessGuids?.find(x => x.toUpperCase()==guidUpper);

      if (_guid != undefined)
        return true;

      if (doTrueCheck)
        return false;
      
      this.curUser=this.authenticationService.currentUserObject();

      if (this.curUser != undefined &&
          this.curUser.isUtelize &&
          this.configSystemService.getValueAsBoolean(ConfigSystemParams.UtelizeHighAccess, false)) {
        return true;
      }

      return this.curUser != undefined &&
             this.curUser.systemAdmin != undefined &&
             this.curUser.systemAdmin &&
             this.configSystemService.getValueAsBoolean(ConfigSystemParams.SystemAdminFullAccess, false);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }
}
